/**
 * Real-time WebSocket Client
 *
 * WebSocket client with automatic reconnection, message queuing,
 * and event handling based on realtime-events.md spec.
 */

import type {
  ConnectionState,
  RealtimeConfig,
  ClientMessage,
  ServerMessage,
  EventMessage,
  RealtimeEvent,
  PresenceData,
} from './types';
import { DEFAULT_CONFIG, ERROR_CODES } from './types';

type EventHandler = (event: EventMessage) => void;
type ConnectionHandler = (state: ConnectionState) => void;
type ErrorHandler = (error: { code: number; message: string }) => void;

interface Subscription {
  room: string;
  lastSequence: number;
}

export class RealtimeClient {
  private config: Required<RealtimeConfig>;
  private ws: WebSocket | null = null;
  private connectionState: ConnectionState = 'disconnected';
  private connectionId: string | null = null;
  private userId: string | null = null;
  private reconnectAttempt = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private connectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastPongTime = 0;

  // Subscriptions and handlers
  private subscriptions = new Map<string, Subscription>();
  private eventHandlers = new Set<EventHandler>();
  private connectionHandlers = new Set<ConnectionHandler>();
  private errorHandlers = new Set<ErrorHandler>();
  private pendingMessages: ClientMessage[] = [];

  // Presence state
  private presenceData: PresenceData | null = null;

  constructor(config: RealtimeConfig) {
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
    };
  }

  /**
   * Connect to the WebSocket server
   */
  connect(): void {
    if (this.connectionState === 'connected' || this.connectionState === 'connecting') {
      return;
    }

    this.setConnectionState('connecting');
    this.clearReconnectTimeout();

    try {
      // Create WebSocket with token in query parameter
      const url = new URL(this.config.url);
      url.searchParams.set('access_token', this.config.token);
      this.ws = new WebSocket(url.toString());

      // Set connection timeout
      this.connectionTimeout = setTimeout(() => {
        if (this.connectionState === 'connecting') {
          this.ws?.close();
          this.handleConnectionFailure();
        }
      }, this.config.connectionTimeout);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
    } catch (error) {
      console.error('[RealtimeClient] Connection error:', error);
      this.handleConnectionFailure();
    }
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.clearTimers();
    this.subscriptions.clear();
    this.pendingMessages = [];

    if (this.ws) {
      this.ws.onclose = null; // Prevent reconnection
      this.ws.close();
      this.ws = null;
    }

    this.setConnectionState('disconnected');
  }

  /**
   * Subscribe to a room
   */
  subscribe(room: string): void {
    if (this.subscriptions.has(room)) {
      return;
    }

    // Max 10 subscriptions
    if (this.subscriptions.size >= 10) {
      console.warn('[RealtimeClient] Maximum subscriptions reached');
      return;
    }

    this.subscriptions.set(room, { room, lastSequence: 0 });

    if (this.connectionState === 'connected') {
      this.send({ type: 'subscribe', room });
    }
  }

  /**
   * Unsubscribe from a room
   */
  unsubscribe(room: string): void {
    if (!this.subscriptions.has(room)) {
      return;
    }

    this.subscriptions.delete(room);

    if (this.connectionState === 'connected') {
      this.send({ type: 'unsubscribe', room });
    }
  }

  /**
   * Request missed events since a sequence number
   */
  sync(room: string, sinceSequence: number): void {
    if (this.connectionState !== 'connected') {
      return;
    }

    this.send({
      type: 'sync',
      room,
      since_sequence: sinceSequence,
    });
  }

  /**
   * Update presence data
   */
  updatePresence(room: string, data: Partial<PresenceData>): void {
    if (this.connectionState !== 'connected' || !this.userId) {
      return;
    }

    this.presenceData = {
      user_id: this.userId,
      name: data.name || this.presenceData?.name || 'Unknown',
      status: data.status || this.presenceData?.status || 'online',
      last_seen: new Date().toISOString(),
      ...data,
    };

    this.send({
      type: 'presence.update',
      room,
      payload: this.presenceData,
    });
  }

  /**
   * Send cursor selection update
   */
  selectCursor(
    room: string,
    selection: { item_id?: string; column_id?: string; cell_range?: { start: string; end: string } }
  ): void {
    if (this.connectionState !== 'connected') {
      return;
    }

    this.send({
      type: 'cursor.select',
      room,
      payload: selection,
    });
  }

  /**
   * Send typing indicator start
   */
  startTyping(room: string, context: { update_id?: string; item_id?: string }): void {
    if (this.connectionState !== 'connected') {
      return;
    }

    this.send({
      type: 'typing.start',
      room,
      payload: context,
    });
  }

  /**
   * Send typing indicator stop
   */
  stopTyping(room: string, context: { update_id?: string; item_id?: string }): void {
    if (this.connectionState !== 'connected') {
      return;
    }

    this.send({
      type: 'typing.stop',
      room,
      payload: context,
    });
  }

  /**
   * Register event handler
   */
  onEvent(handler: EventHandler): () => void {
    this.eventHandlers.add(handler);
    return () => this.eventHandlers.delete(handler);
  }

  /**
   * Register connection state handler
   */
  onConnectionChange(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler);
    // Immediately call with current state
    handler(this.connectionState);
    return () => this.connectionHandlers.delete(handler);
  }

  /**
   * Register error handler
   */
  onError(handler: ErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Get subscribed rooms
   */
  getSubscriptions(): string[] {
    return Array.from(this.subscriptions.keys());
  }

  /**
   * Get last sequence number for a room
   */
  getLastSequence(room: string): number {
    return this.subscriptions.get(room)?.lastSequence ?? 0;
  }

  /**
   * Update token (for token refresh)
   */
  updateToken(token: string): void {
    this.config.token = token;
    // Reconnect with new token if currently connected
    if (this.connectionState === 'connected') {
      this.disconnect();
      this.connect();
    }
  }

  // Private methods

  private handleOpen(): void {
    this.clearConnectionTimeout();
    this.reconnectAttempt = 0;
    this.lastPongTime = Date.now();
    // Connection state will be set to 'connected' after receiving 'connected' message
  }

  private handleClose(event: CloseEvent): void {
    this.clearTimers();
    this.ws = null;

    if (event.wasClean) {
      this.setConnectionState('disconnected');
    } else {
      this.handleConnectionFailure();
    }
  }

  private handleError(error: Event): void {
    console.error('[RealtimeClient] WebSocket error:', error);
    this.errorHandlers.forEach((handler) =>
      handler({ code: ERROR_CODES.UNAUTHORIZED, message: 'Connection error' })
    );
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message = JSON.parse(event.data) as ServerMessage;
      this.processMessage(message);
    } catch (error) {
      console.error('[RealtimeClient] Failed to parse message:', error);
    }
  }

  private processMessage(message: ServerMessage): void {
    switch (message.type) {
      case 'connected':
        this.connectionId = message.payload.connection_id;
        this.userId = message.payload.user_id;
        this.setConnectionState('connected');
        this.startPingInterval();
        this.resubscribeAll();
        this.flushPendingMessages();
        break;

      case 'subscribed': {
        // Subscription confirmed - request missed events if we have a sequence
        const sub = this.subscriptions.get(message.room);
        if (sub && sub.lastSequence > 0) {
          this.sync(message.room, sub.lastSequence);
        }
        break;
      }

      case 'unsubscribed':
        // Unsubscription confirmed
        break;

      case 'pong':
        this.lastPongTime = Date.now();
        break;

      case 'error':
        console.error('[RealtimeClient] Server error:', message.error);
        this.errorHandlers.forEach((handler) => handler(message.error));

        // Handle specific error codes
        if (message.error.code === ERROR_CODES.UNAUTHORIZED) {
          this.disconnect();
        }
        break;

      case 'presence_state':
        // Initial presence state for a room - could emit as event
        break;

      case 'sync_response':
        this.processSyncResponse(message);
        break;

      case 'event':
        this.processEvent(message);
        break;
    }
  }

  private processEvent(message: EventMessage): void {
    // Update last sequence for the room
    const sub = this.subscriptions.get(message.room);
    if (sub) {
      sub.lastSequence = message.sequence;
    }

    // Dispatch to handlers
    this.eventHandlers.forEach((handler) => handler(message));
  }

  private processSyncResponse(message: {
    room: string;
    events: RealtimeEvent[];
    has_more: boolean;
    error?: string;
  }): void {
    if (message.error === 'sequence_too_old') {
      // Need full state refresh - emit special event
      this.eventHandlers.forEach((handler) =>
        handler({
          type: 'event',
          event: 'sync.full_refresh_needed',
          room: message.room,
          payload: { room: message.room },
          sequence: 0,
        })
      );
      return;
    }

    // Process buffered events in order
    for (const event of message.events) {
      this.processEvent({
        type: 'event',
        event: event.event,
        room: event.room,
        payload: event.payload,
        sequence: event.sequence,
        timestamp: event.timestamp,
      });
    }

    // Request more if available
    if (message.has_more) {
      const lastSeq = message.events[message.events.length - 1]?.sequence ?? 0;
      this.sync(message.room, lastSeq);
    }
  }

  private send(message: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      this.pendingMessages.push(message);
    }
  }

  private resubscribeAll(): void {
    for (const room of this.subscriptions.keys()) {
      this.send({ type: 'subscribe', room });
    }
  }

  private flushPendingMessages(): void {
    const messages = this.pendingMessages;
    this.pendingMessages = [];
    for (const message of messages) {
      this.send(message);
    }
  }

  private setConnectionState(state: ConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.connectionHandlers.forEach((handler) => handler(state));
    }
  }

  private handleConnectionFailure(): void {
    this.setConnectionState('reconnecting');
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    const delay = this.calculateReconnectDelay();
    this.reconnectAttempt++;

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private calculateReconnectDelay(): number {
    // Exponential backoff with jitter: delay * (0.5 + Math.random())
    const baseDelay = this.config.reconnectBaseDelay;
    const maxDelay = this.config.reconnectMaxDelay;
    const exponentialDelay = Math.min(
      baseDelay * Math.pow(2, this.reconnectAttempt),
      maxDelay
    );
    const jitter = 0.5 + Math.random();
    return Math.floor(exponentialDelay * jitter);
  }

  private startPingInterval(): void {
    this.pingInterval = setInterval(() => {
      // Check for dead connection
      const timeSinceLastPong = Date.now() - this.lastPongTime;
      if (timeSinceLastPong > this.config.pingInterval + 5000) {
        console.warn('[RealtimeClient] Connection appears dead, reconnecting');
        this.ws?.close();
        return;
      }

      this.send({ type: 'ping' });
    }, this.config.pingInterval);
  }

  private clearTimers(): void {
    this.clearReconnectTimeout();
    this.clearConnectionTimeout();
    this.clearPingInterval();
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
  }

  private clearPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}

// Singleton instance for the app
let clientInstance: RealtimeClient | null = null;

export function getRealtimeClient(): RealtimeClient | null {
  return clientInstance;
}

export function createRealtimeClient(config: RealtimeConfig): RealtimeClient {
  if (clientInstance) {
    clientInstance.disconnect();
  }
  clientInstance = new RealtimeClient(config);
  return clientInstance;
}

export function destroyRealtimeClient(): void {
  if (clientInstance) {
    clientInstance.disconnect();
    clientInstance = null;
  }
}
